/** The viewer's path, answered outside the app's routes. */
export const FILE_VIEWER_PATH = "/file-viewer";

const READY = "eventr-file-viewer-ready";
const FILE = "eventr-file-viewer-file";
const FAILED = "eventr-file-viewer-failed";

export type FileViewerMessage =
  { type: typeof READY } | { type: typeof FILE; blob: Blob; name: string } | { type: typeof FAILED };

export interface FileViewerHandle {
  show: (blob: Blob, name: string) => void;
  fail: () => void;
}

/**
 * Opens the viewer tab. Call it during the click, before any await; the file follows once the
 * tab says it is ready. Undefined when the browser blocks the tab.
 */
export function openFileViewer(): FileViewerHandle | undefined {
  const tab = window.open(FILE_VIEWER_PATH, "_blank");
  if (!tab) return undefined;

  let ready = false;
  let pending: FileViewerMessage | undefined;
  const send = (message: FileViewerMessage) => {
    if (ready) tab.postMessage(message, window.location.origin);
    else pending = message;
  };

  const onMessage = (event: MessageEvent<FileViewerMessage>) => {
    if (event.source !== tab || event.origin !== window.location.origin || event.data?.type !== READY) return;
    ready = true;
    window.removeEventListener("message", onMessage);
    if (pending) tab.postMessage(pending, window.location.origin);
  };
  window.addEventListener("message", onMessage);

  return {
    show: (blob, name) => send({ type: FILE, blob, name }),
    fail: () => send({ type: FAILED }),
  };
}

/** In the viewer tab: asks the opener for the file, and calls back with it or with null. */
export function receiveFile(onFile: (file: { blob: Blob; name: string } | null) => void): () => void {
  const onMessage = (event: MessageEvent<FileViewerMessage>) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === FILE) onFile({ blob: event.data.blob, name: event.data.name });
    if (event.data?.type === FAILED) onFile(null);
  };
  window.addEventListener("message", onMessage);
  window.opener?.postMessage({ type: READY } satisfies FileViewerMessage, window.location.origin);
  if (!window.opener) onFile(null);
  return () => window.removeEventListener("message", onMessage);
}
